import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ScrollText, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";

const PLAY_LOG_DEFAULT_WIDTH = 240;
const PLAY_LOG_MAX_WIDTH_RATIO = 0.5;
const PLAY_LOG_LS_KEY = "junkball_playLogWidth";
const PLAY_LOG_COLLAPSE_AT = 80;

// Infer a kind string from the log description for local-play entries that
// don't carry a server-supplied log_kind.
function inferKindFromDesc(desc) {
  if (!desc) return null;
  const u = desc.toUpperCase();
  if (u.startsWith("STRIKEOUT"))                                  return "k";
  if (u.includes("HOME RUN"))                                     return "run";
  if (u.startsWith("SINGLE") || u.startsWith("DOUBLE") || u.startsWith("TRIPLE")) return "hit";
  if (
    u.startsWith("WALK") || u.startsWith("HIT BY PITCH") ||
    u.startsWith("BOGO WALK") || u.startsWith("TAKE THE WALK") ||
    u.startsWith("BALK") || u.startsWith("PLUNK")
  )                                                               return "walk";
  if (u.startsWith("STOLEN BASE") || u.startsWith("STEAL HOME")) return "steal";
  if (u.startsWith("RUNDOWN"))                                    return "rundown";
  if (
    u.startsWith("CAUGHT STEALING") ||
    u.includes("DOUBLE PLAY") || u.includes("TRIPLE PLAY") ||
    u.startsWith("OUT —") || u.startsWith("OUT!")
  )                                                               return "caught";
  if (u.startsWith("BALL IN PLAY"))                              return "inplay";
  // "STRIKE —" is a non-terminal strike; "FOUL BALL" is a foul; "BIRD STRIKE!" is card
  if (u.startsWith("FOUL BALL") || u.startsWith("BIRD STRIKE"))  return "pitch";
  if (u.startsWith("STRIKE"))                                     return "pitch";
  if (u.startsWith("BALL "))                                      return "ball";
  if (u.startsWith("WILD PITCH"))                                 return "ball";
  // Manager card / event entries
  if (
    u.startsWith("CALLING IN RELIEF") || u.startsWith("CALL THE BULLPEN") ||
    u.startsWith("PRIME THE PUMP")    || u.startsWith("BENCH CLEARING") ||
    u.startsWith("CLUTCH GENE")       || u.startsWith("QUICK RELEASE") ||
    u.startsWith("TENSIONS HIGH")     || u.startsWith("CHARGE THE MOUND") ||
    u.startsWith("LINEUP PROTECTION") || u.startsWith("HOT WHEELS") ||
    u.startsWith("FREE BASE")         || u.startsWith("DIG IT OUT") ||
    u.startsWith("MANAGER")           || u.includes(" CARD") ||
    u.startsWith("BUNT")              || u.startsWith("STEAL")
  )                                                               return "card";
  return null;
}

// Headline colour by outcome kind.
const KIND_COLOR = {
  run:    "text-amber-300",
  k:      "text-rose-400",
  hit:    "text-emerald-400",
  walk:   "text-sky-400",
  steal:   "text-violet-400",
  rundown: "text-orange-400",
  caught:  "text-slate-400",
  card:   "text-amber-200/80",
  out:    "text-slate-400",
  inplay: "text-slate-300",
  pitch:  "text-slate-500",
  ball:   "text-slate-500",
};

// Inning + outs badge — shows "T3" with the out-count at play time below it.
function InningBadge({ inning, half, outs }) {
  return (
    <div className="shrink-0 flex flex-col items-center gap-0 min-w-[22px]">
      <span className="text-[9px] font-mono font-bold px-1 py-0.5 rounded bg-slate-800 text-slate-400 leading-tight whitespace-nowrap w-full text-center">
        {half === "top" ? "T" : "B"}{inning ?? "?"}
      </span>
      {outs != null && (
        <span className="text-[8px] text-slate-600 font-mono leading-none mt-0.5">
          {outs}o
        </span>
      )}
    </div>
  );
}

const ORDINAL = (n) => {
  if (!n && n !== 0) return "";
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

function InningDivider({ inning, half }) {
  const label = `${half === "top" ? "Top" : "Bottom"} of ${ORDINAL(inning)}`;
  return (
    <div className="flex items-center gap-2 px-1 pt-3 pb-1 first:pt-1">
      <span className="text-[9px] font-black uppercase tracking-widest text-amber-400/80 shrink-0">
        {label}
      </span>
      <div className="flex-1 h-px bg-amber-500/20" />
    </div>
  );
}

// Parse a log desc into { outcome, subject, detail }.
//
// Desc formats from the server/client:
//   "STRIKEOUT! Ramirez"
//   "STRIKE — Ramirez (P 7+2=9 vs B 3 · chip FB/CH miss)"
//   "HOME RUN! Ramirez (3 RBI)"
//   "WALK! Ramirez takes 1B"
//   "BALL IN PLAY — Ramirez contact 3 (P 8 vs B 6)"
//   "SINGLE — Ramirez (contact +3, P 8 vs B 5)"
//   "Wild Pitch! +1 Ball"
//   "HOT WHEELS — runners advance one base (no scoring)"
//
// outcome = result label before " — " or "!"  (line 1, coloured, bold)
// subject = player name / brief action          (line 2, muted)
// detail  = everything in the trailing parens   (line 3, dimmer — always shown)
function parseLogDesc(desc) {
  if (!desc) return { outcome: "", subject: null, detail: null };

  // Strip trailing parenthetical from the core text so subject parsing stays clean.
  const parenAt = desc.lastIndexOf(" (");
  const detail = parenAt > 0 && desc.endsWith(")") ? desc.slice(parenAt + 2, -1) : null;
  const core = parenAt > 0 && desc.endsWith(")") ? desc.slice(0, parenAt).trim() : desc;

  // Split core on " — " (em-dash) or "!" to separate outcome from subject.
  const dashAt = core.indexOf(" — ");
  const bangAt = core.indexOf("!");

  let outcome, subject;
  if (dashAt > 0) {
    outcome = core.slice(0, dashAt).trim();
    subject = core.slice(dashAt + 3).trim() || null;
  } else if (bangAt > 0) {
    outcome = core.slice(0, bangAt).trim();
    const afterBang = core.slice(bangAt + 1).trim();
    subject = afterBang || null;
  } else {
    outcome = core;
    subject = null;
  }

  return { outcome, subject, detail };
}

function LogEntry({ entry, isLatest }) {
  // Resolve kind: server-supplied wins; fall back to desc inference.
  const kind = entry.kind || inferKindFromDesc(entry.desc);
  const { outcome, subject, detail } = parseLogDesc(entry.desc);

  // Outcome colour: kind-based. Latest entry gets amber fallback.
  const outcomeColor = KIND_COLOR[kind] || (isLatest ? "text-amber-300" : "text-slate-300");

  return (
    <div
      data-testid={`log-entry-${entry.id}`}
      title={entry.desc || ""}
      className={`flex items-start gap-2 px-2 py-2 rounded-md transition-colors ${
        isLatest ? "bg-amber-500/10 border border-amber-500/20" : "hover:bg-slate-800/50"
      }`}
    >
      <InningBadge inning={entry.inning} half={entry.half} outs={entry.outs} />
      <div className="flex-1 min-w-0">
        {/* Line 1 — outcome label. break-words so it wraps at any drawer width. */}
        <p className={`text-[11px] leading-tight font-bold break-words ${outcomeColor}`}>
          {outcome}
        </p>
        {/* Line 2 — player / action (e.g. "Ramirez", "runners advance one base"). */}
        {subject && (
          <p className="text-[10px] leading-snug text-slate-300 mt-0.5 break-words">
            {subject}
          </p>
        )}
        {/* Line 3 — math resolution: dice, chips, caps. */}
        {detail && (
          <p className="text-[9px] leading-snug text-slate-500 mt-0.5 break-words font-mono">
            {detail}
          </p>
        )}
        {/* Line 4 — card contributions with +/- modifiers ("P: +2 Amped Up | B: +1 Rally Caps"). */}
        {entry.cards && (
          <p className="text-[9px] leading-snug text-amber-500/70 mt-0.5 break-words">
            {entry.cards}
          </p>
        )}
      </div>
    </div>
  );
}

export default function PlayByPlayDrawer({ log = [], onClear }) {
  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(PLAY_LOG_LS_KEY) || "", 10);
      if (!isNaN(v) && v > 0) return v;
    } catch {}
    return PLAY_LOG_DEFAULT_WIDTH;
  });
  useEffect(() => {
    try { localStorage.setItem(PLAY_LOG_LS_KEY, String(width)); } catch {}
  }, [width]);

  const startResize = useCallback((evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    const isTouch = evt.type === "touchstart";
    const startX = isTouch ? evt.touches[0].clientX : evt.clientX;
    const startW = width;
    const maxW = Math.floor(window.innerWidth * PLAY_LOG_MAX_WIDTH_RATIO);
    const cleanup = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    const onMove = (e) => {
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      const delta = x - startX;
      const proposed = startW + delta;
      if (proposed < PLAY_LOG_COLLAPSE_AT) {
        setOpen(false);
        setWidth(PLAY_LOG_DEFAULT_WIDTH);
        cleanup();
        return;
      }
      setWidth(Math.min(maxW, proposed));
    };
    const onUp = () => cleanup();
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
  }, [width]);

  return (
    <div className="relative h-full flex" data-testid="play-by-play-drawer">
      {/* Toggle button */}
      <button
        onClick={() => setOpen(prev => !prev)}
        className="absolute top-2 left-2 z-30 flex items-center gap-1 px-2 py-1.5 rounded-lg bg-slate-800/80 hover:bg-slate-700 border border-slate-700/50 text-slate-400 hover:text-amber-400 transition-colors backdrop-blur-sm"
        data-testid="toggle-play-log"
        title={open ? "Hide play log" : "Show play log"}
      >
        <ScrollText size={14} />
        {!open && log.length > 0 && (
          <span className="text-[9px] font-bold text-amber-400 ml-0.5">{log.length}</span>
        )}
        {open ? <ChevronLeft size={12} /> : <ChevronRight size={12} />}
      </button>

      {/* Drawer panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 35 }}
            className="relative h-full bg-slate-900/95 border-r border-slate-800 backdrop-blur-sm flex flex-col overflow-hidden"
            data-testid="play-log-panel"
            style={{ width }}
          >
            {/* Resize handle */}
            <div
              role="separator"
              aria-orientation="vertical"
              title="Drag to resize"
              onMouseDown={startResize}
              onTouchStart={startResize}
              className="absolute top-0 right-0 bottom-0 w-2 z-30 cursor-col-resize group"
            >
              <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-1 h-10 rounded-full bg-slate-700 group-hover:bg-amber-500/60 transition-colors" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800 shrink-0">
              <span className="text-[10px] font-black tracking-widest text-slate-400">PLAY LOG</span>
              {log.length > 0 && (
                <button
                  onClick={onClear}
                  title="Clear log"
                  className="p-1 rounded hover:bg-slate-800 text-slate-600 hover:text-red-400 transition-colors"
                  data-testid="clear-log-btn"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>

            {/* Entries — grouped by half-inning, newest group first. Within each
                group entries are in chronological order. */}
            <div className="flex-1 overflow-y-auto p-1.5 space-y-0.5">
              {log.length === 0 ? (
                <div className="text-center py-8 text-slate-600 text-[10px]">
                  No plays recorded yet
                </div>
              ) : (() => {
                const groupOrder = [];
                const groupMap = {};
                for (const entry of log) {
                  const key = `${entry.inning}-${entry.half}`;
                  if (!groupMap[key]) {
                    groupMap[key] = { key, inning: entry.inning, half: entry.half, entries: [] };
                    groupOrder.push(groupMap[key]);
                  }
                  groupMap[key].entries.push(entry);
                }
                groupOrder.reverse();
                const latestId = log[log.length - 1]?.id;
                const items = [];
                for (const group of groupOrder) {
                  items.push(
                    <InningDivider key={`div-${group.key}`} inning={group.inning} half={group.half} />
                  );
                  for (const entry of [...group.entries].reverse()) {
                    items.push(
                      <LogEntry key={entry.id} entry={entry} isLatest={entry.id === latestId} />
                    );
                  }
                }
                return items;
              })()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
